import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Channel, ChannelService, ChannelTypeEnum } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'channel-edit-component',
    // templateUrl: './channel-edit.component.html',
    templateUrl: './channel-edit.v2.component.html',
    styleUrls: ['./channel-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelEditComponent extends AbstractComponent {

  channel: Channel;
  channels: Array<Channel> = new Array<Channel>();

  cloudAPI: ChannelTypeEnum = ChannelTypeEnum.CLOUD_API;
  zAPI: ChannelTypeEnum = ChannelTypeEnum.Z_API;
  webchatAPI: ChannelTypeEnum = ChannelTypeEnum.WEBCHAT_API;

  constructor(
    injector: Injector,
    public channelService: ChannelService,
    public dialogRef: MatDialogRef<ChannelEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Channel,
  ) {
    super(injector);
    this.channel = { credentials: {} } as Channel;
  }

  ngOnInit() {
    this.getChannelById(this.data?._id);
    this.createForm();
  }

  private createForm(): void {
    if (this.data?.type === ChannelTypeEnum.CLOUD_API) {
      this.formGroup = new FormGroup({
        name: new FormControl(null, [Validators.required]),
        type: new FormControl(null, [Validators.required]),
        integration: new FormControl(true),
        credentials: new FormGroup({
          token: new FormControl(null, [Validators.required]),
          phoneNumberID: new FormControl(null, [Validators.required]),
          whatsAppBusinessAccountID: new FormControl(null, [Validators.required]),
        }),
      });
    } else if (this.data?.type === ChannelTypeEnum.RCS_API) {
      this.formGroup = new FormGroup({
        name: new FormControl(null, [Validators.required]),
        type: new FormControl(null, [Validators.required]),
        integration: new FormControl(true),
        // The account only. The provider's client secret is one token for the whole Pontal
        // organization, so the server stamps it from its own environment on every save and the
        // operator is never asked for it.
        credentials: new FormGroup({
          account: new FormControl(null, []),
        }),
        // persisted into channel.metadata (same shape as channel-form). Every RCS channel is
        // conversational, so the value is fixed here and its switch is commented out in the template.
        rcs: new FormGroup({
          conversational: new FormControl(true),
        }),
      });
    } else if (this.data?.type === ChannelTypeEnum.WEBCHAT_API) {
      this.formGroup = new FormGroup({
        name: new FormControl(null, [Validators.required]),
        type: new FormControl(null, [Validators.required]),
        integration: new FormControl(true),
        // widget customization — persisted into channel.metadata (same shape as channel-form)
        webchat: new FormGroup({
          primaryColor: new FormControl(null, []),
          logoUrl: new FormControl(null, []),
          buttonPosition: new FormControl('right', []),
          privacyPolicyUrl: new FormControl(null, []),
        }),
      });
    } else {
      this.formGroup = new FormGroup({
        name: new FormControl(null, [Validators.required]),
        type: new FormControl(null, [Validators.required]),
        integration: new FormControl(true),
      });
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private getChannelById(idChannel: string) {
    this.channelService.getById(idChannel)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => {
          this.channel = channel;
          this.formGroup.patchValue(this.channel);
          this.formGroup.get('integration').setValue(false);
          // RCS: `conversational` is not rehydrated from the stored channel on purpose — it is
          // always true, so a channel saved before this change is corrected on the next save.
          if (channel.type === ChannelTypeEnum.WEBCHAT_API) {
            this.formGroup.get('webchat').patchValue(channel.metadata ?? {});
          }
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  save() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    const payload: Channel = { ...this.formGroup.value };
    // RCS: the conversational capability lives in channel.metadata (never a top-level "rcs" field)
    if (this.channel.type === ChannelTypeEnum.RCS_API) {
      payload.metadata = { ...(this.channel.metadata ?? {}), ...this.formGroup.get('rcs').value };
      delete (payload as any).rcs;
    }
    // Webchat: customization lives in channel.metadata (never a top-level "webchat" field)
    if (this.channel.type === ChannelTypeEnum.WEBCHAT_API) {
      payload.metadata = { ...(this.channel.metadata ?? {}), ...this.formGroup.get('webchat').value };
      delete (payload as any).webchat;
    }

    this.channelService.update(this.channel._id, payload).subscribe({
      next: () => {
        this.alertService.success(this.t('channel.edit.channelUpdatedSuccess'));
        this.goBack();
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  showSaveButton() {
    return this.isAdmin() && (!this.formGroup.get('integration').value || !this.isCloudAPI());
  }

  getName() {
    return this.formGroup.get('name').value;
  }

  private isCloudAPI() {
    return this.formGroup.get('type').value === ChannelTypeEnum.CLOUD_API;
  }
}
